import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Leaf, Menu, X, LayoutDashboard, Pill, MapPin, LineChart, BookOpen, Bell, Users, MessagesSquare, Shield, LogOut, UserPlus, LogIn, Lock, Mail, Settings, Building2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { CrisisButton } from "./CrisisButton";
import { AccountMenu } from "./AccountMenu";

// Public (logged-out) top nav
const publicLinks = [
  { to: "/taper", label: "Taper Tool", icon: Pill },
  { to: "/find-support", label: "Find Support", icon: MapPin },
  { to: "/articles", label: "Articles", icon: BookOpen },
  { to: "/contact", label: "Contact", icon: Mail },
];

// Account-gated tools shown (with a lock) to logged-out users — clicking prompts sign-up
const gatedLinks = [
  { label: "Progress", icon: LineChart },
  { label: "Reminders", icon: Bell },
  { label: "Buddy", icon: Users },
  { label: "Community", icon: MessagesSquare },
];

// Full app nav (mirrors Layout) shown to signed-in users for consistency
const appLinks = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/taper", label: "Taper Tool", icon: Pill },
  { to: "/app/locator", label: "Find Support", icon: MapPin },
  { to: "/app/tracker", label: "Progress", icon: LineChart },
  { to: "/app/reminders", label: "Reminders", icon: Bell },
  { to: "/app/buddy", label: "Buddy", icon: Users },
  { to: "/app/community", label: "Community", icon: MessagesSquare },
  { to: "/app/learn", label: "Learn", icon: BookOpen },
  { to: "/contact", label: "Contact", icon: Mail },
];

export const PublicShell = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const DesktopNav = () => (
    <>
      {publicLinks.map((l) => (
        <NavLink key={l.to} to={l.to} data-testid={`public-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
          className={({ isActive }) =>
            `rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-[#4A7c59]/10 text-[#4A7c59]" : "text-[#2C363F] hover:bg-[#4A7c59]/10"}`}>
          {l.label}
        </NavLink>
      ))}
    </>
  );

  const doLogout = () => { setOpen(false); logout(); navigate("/"); };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-3 sm:px-6">
          <Link to="/" data-testid="public-logo" className="flex shrink-0 items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#4A7c59] text-white"><Leaf strokeWidth={1.8} className="h-5 w-5" /></div>
            <span className="font-head text-xl font-bold text-[#2C363F]">TaperPath</span>
          </Link>
          <nav className="hidden items-center gap-1 md:flex"><DesktopNav /></nav>
          <div className="hidden shrink-0 items-center gap-3 md:flex">
            {user ? (
              <>
                <Link to="/app" data-testid="public-dashboard-btn" className="rounded-full bg-[#4A7c59] px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5">Dashboard</Link>
                <AccountMenu />
              </>
            ) : (
              <Link to="/auth" data-testid="public-signin-btn" className="rounded-full bg-[#4A7c59] px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5">Sign in</Link>
            )}
          </div>
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="public-menu-toggle">{open ? <X /> : <Menu />}</button>
        </div>

        {open && (
          <div className="space-y-1 border-t border-slate-200 px-4 py-3 md:hidden" data-testid="public-mobile-menu">
            {user ? (
              <>
                {appLinks.map((l) => (
                  <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-[#4A7c59] text-white" : "text-[#2C363F] hover:bg-[#4A7c59]/10"}`}>
                    <l.icon strokeWidth={1.8} className="h-4.5 w-4.5" /> {l.label}
                  </NavLink>
                ))}
                <NavLink to="/app/settings" onClick={() => setOpen(false)} data-testid="mobile-nav-settings"
                  className={({ isActive }) => `flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-[#4A7c59] text-white" : "text-[#2C363F] hover:bg-[#4A7c59]/10"}`}>
                  <Settings strokeWidth={1.8} className="h-4.5 w-4.5" /> Account Settings
                </NavLink>
                {user.manages_centres && (
                  <NavLink to="/app/my-centre" onClick={() => setOpen(false)} data-testid="mobile-nav-mycentre"
                    className={({ isActive }) => `flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-[#4A7c59] text-white" : "text-[#2C363F] hover:bg-[#4A7c59]/10"}`}>
                    <Building2 strokeWidth={1.8} className="h-4.5 w-4.5" /> My Centre
                  </NavLink>
                )}
                {user.role === "admin" && (
                  <NavLink to="/app/admin" onClick={() => setOpen(false)} data-testid="mobile-nav-admin"
                    className={({ isActive }) => `flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-[#4A7c59] text-white" : "text-[#2C363F] hover:bg-[#4A7c59]/10"}`}>
                    <Shield strokeWidth={1.8} className="h-4.5 w-4.5" /> Admin
                  </NavLink>
                )}
                <button onClick={doLogout} data-testid="mobile-logout" className="flex w-full items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted"><LogOut className="h-4.5 w-4.5" /> Sign out</button>
              </>
            ) : (
              <>
                {publicLinks.map((l) => (
                  <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-[#4A7c59]/10 text-[#4A7c59]" : "text-[#2C363F] hover:bg-[#4A7c59]/10"}`}>
                    <l.icon strokeWidth={1.8} className="h-4.5 w-4.5" /> {l.label}
                  </NavLink>
                ))}
                <p className="px-4 pb-1 pt-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">Free account needed</p>
                {gatedLinks.map((l) => (
                  <Link key={l.label} to="/auth" onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="flex items-center justify-between rounded-full px-4 py-2.5 text-sm font-medium text-[#2C363F] transition-colors duration-200 hover:bg-[#4A7c59]/10">
                    <span className="flex items-center gap-3"><l.icon strokeWidth={1.8} className="h-4.5 w-4.5" /> {l.label}</span>
                    <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                  </Link>
                ))}
                <div className="mt-2 flex flex-col gap-2 border-t border-slate-200 pt-3">
                  <Link to="/auth" onClick={() => setOpen(false)} data-testid="mobile-signin" className="flex items-center justify-center gap-2 rounded-full border border-[#4A7c59]/30 bg-white px-4 py-2.5 text-sm font-semibold text-[#4A7c59]"><LogIn className="h-4 w-4" /> Sign in</Link>
                  <Link to="/auth" onClick={() => setOpen(false)} data-testid="mobile-signup" className="flex items-center justify-center gap-2 rounded-full bg-[#4A7c59] px-4 py-2.5 text-sm font-semibold text-white"><UserPlus className="h-4 w-4" /> Create free account</Link>
                </div>
              </>
            )}
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="mt-16 border-t border-border py-10 text-center text-sm text-muted-foreground">
        <p>TaperPath is an educational harm-reduction tool, not a replacement for medical care.</p>
        <p className="mt-1">Reference: <a href="https://benzo.org.uk/manual" target="_blank" rel="noreferrer" className="text-[#4A7c59] underline">The Ashton Manual (benzo.org.uk/manual)</a></p>
        <p className="mt-3 flex flex-wrap items-center justify-center gap-4">
          <Link to="/taper" className="hover:text-[#4A7c59]">Taper Tool</Link>
          <Link to="/find-support" className="hover:text-[#4A7c59]">Find Support</Link>
          <Link to="/articles" className="hover:text-[#4A7c59]">Articles</Link>
          <Link to="/contact" className="hover:text-[#4A7c59]">Contact</Link>
          {user ? <Link to="/app" className="hover:text-[#4A7c59]">Dashboard</Link> : <Link to="/auth" className="hover:text-[#4A7c59]">Sign in</Link>}
        </p>
      </footer>

      <CrisisButton />
    </div>
  );
};
